import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { RootState } from "@/redux/store";

// Interfaces
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// MUI
import { Grid, Skeleton, Typography } from "@mui/material";

// Context
import { useMyCitContextData } from "@/DataPages/context/myCit/MyCitContext";

// API
import { getUsers } from "@Common/api/users";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import MyCitAnalytics from "./MyCitAnalytics";
import MyCITDataTable from "./MyCITDataTable";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

interface IMyCitModelProps {
	selected: IPeriodTreeSelection;
	dataPresent: boolean;
	isUpdatable: boolean;
	isLoading: boolean;
}

export const MyCitModel = (props: Readonly<IMyCitModelProps>) => {
	const { deals } = useMyCitContextData();

	const locationId = useSelector((state: RootState) => state.location.location?.locationId ?? "");

	const { data: financeManagers = [], isLoading: allFinanceManagersLoading } = useQuery({
		queryKey: ["allFinanceManagers", locationId],
		queryFn: async () => {
			const data = await getUsers(locationId, "F&I Manager");
			if (!data) {
				throw new Error("Error getting the finance managers");
			}
			return data.filter((fm) => fm.currentStatus?.status === "ACTIVE");
		},
		staleTime: Infinity,
	});

	const isLoading = or(props.isLoading, allFinanceManagersLoading);

	return (
		<>
			<RoundedBox
				sx={{
					height: "auto !important",
					marginBottom: 2,
				}}
			>
				<MyCitAnalytics title={props.selected?.titleExtended} financeManagers={financeManagers} isLoading={isLoading} />
			</RoundedBox>
			<RoundedBox sx={{ height: "fit-content !important" }}>
				<Grid container alignContent={"flex-start"} sx={{ height: "100%" }}>
					<Grid item xs={12}>
						{isLoading ? (
							<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
								<Grid item>
									<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
								</Grid>
							</Grid>
						) : (
							<>
								{props.selected && (
									<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
										<Grid item>
											<Typography className="page-title" component="div" sx={{ lineHeight: "1.2" }}>
												{`${props.selected?.titleExtended} Contracts In Transit`}
											</Typography>
										</Grid>
									</Grid>
								)}
							</>
						)}
					</Grid>

					<Grid container item>
						<Grid item xs={12} height={600}>
							{isLoading && (
								<Grid item xs={12} mt={1}>
									<TableLoader toolbar buttons={3} content rows={5} multiTitles={5}></TableLoader>
								</Grid>
							)}

							{and(!isLoading, !deals.length) && (
								<EmptyBody
									image={props.dataPresent ? emptyTable : empty}
									title={props.dataPresent ? "No Contracts In Transit to show" : "No Pay Period selected"}
									bodyHeight={props.dataPresent ? "435px" : "510px"}
									sx={{
										boxShadow: "none !important",
									}}
								/>
							)}

							{and(!isLoading, !!deals.length) && <MyCITDataTable />}
						</Grid>
					</Grid>
				</Grid>
			</RoundedBox>
		</>
	);
};
